/**
 * parseExistingBellLine — read a line that IS ALREADY a reminder.
 *
 * Walter 2026-09-26. His screenshot: `🔔 10/15 8am: Steve l: … model PF
 * business?` rendered with its orange date but sorted under UNDATED. The
 * cause is the trailing question mark.
 *
 * `parseReminderLine` refuses a line ending in "?" (reason: 'question') so a
 * note reading "is Wed ok?" never auto-captures. Capture itself switches that
 * off — in a designated reminder list every line is a reminder, so
 * detectReminderOnLine passes `{ allowQuestion, allowEmptyBody }`. Three OTHER
 * paths re-read a line that already carries a 🔔 and never got the same
 * options, so each of them silently demoted a reminder it should have
 * recognised:
 *
 *   1. the reparse in useReminderCapture, which then wrote parsedDate: null —
 *      editing such a line DELETED its date (the bug above, and data loss in
 *      the only sense that matters: his 10/15 was gone from the reminder while
 *      the line still showed it);
 *   2. organizeReminderList's fallback, which classified it "undated";
 *   3. canonicalizeBellLine, so the tidy-up never added the ": " and the line
 *      never went orange.
 *
 * The distinction that matters is not the punctuation, it's provenance: a line
 * with a bell on it, or a doc that already exists for it, was a reminder
 * before we looked. Guessing is over by then. `allowEmptyBody` rides along for
 * the same reason — capture accepts a bare date ("6/6") as a dated, bodyless
 * reminder, so a re-read that rejected one would demote it exactly the same
 * way.
 *
 * Use this for EXISTING reminder lines. Never for deciding whether a plain
 * note should become one — that is detectReminderOnLine's job, and the
 * question guard still belongs there.
 */
import { parseReminderLine } from './reminderParser';

export function parseExistingBellLine(text, opts = {}) {
  return parseReminderLine(text ?? '', { ...opts, allowQuestion: true, allowEmptyBody: true });
}

export default parseExistingBellLine;
